import React from 'react';
import dayjs from 'dayjs';

import { Container, LinearProgress } from '@mui/material';
import ClearFiltersButton from '@components/Utils/ClearFiltersButton';

import { useRooftop, useCustomerConfig } from '@store';

import { Appraisal_Vehicles } from '@gql/base-types';
import { useGetAppraisalRowsQuery } from '../queries/getAppraisalRows.query.generated';
import AppraisalDateRange from './AppraisalDateRange';
import AppraisalIndexTable from './AppraisalIndexTable';

const log = false;

//TODO: Move this to a global location- this is all dashboard filter stuff
const getDefaultDisable = () => {
  const localRange = localStorage.getItem(`appraisal-index-range`);
  if (localRange && localRange === `custom`) return false;
  else return true;
};
const getDefaultRange = () => {
  const localRange = localStorage.getItem(`appraisal-index-range`);
  if (localRange) return localRange;
  else return `this`;
};
const getDefaultStart = () => {
  const localRange = localStorage.getItem(`appraisal-index-range`);
  const localStart = localStorage.getItem(`appraisal-index-start`);
  if (localRange === `custom` && localStart) return localStart;
  else if (localRange === `last`) return dayjs.utc(dayjs().startOf('day').subtract(6, 'months')).format();
  else return dayjs.utc(dayjs().startOf('day').subtract(3, 'months')).format();
};
const getDefaultEnd = () => {
  const localRange = localStorage.getItem(`appraisal-index-range`);
  const localEnd = localStorage.getItem(`appraisal-index-end`);
  if (localRange === `custom` && localEnd) return localEnd;
  else if (localRange === `last`) return dayjs.utc(dayjs().endOf('day').subtract(3, 'months')).format();
  else return dayjs.utc(dayjs().endOf('day')).format();
};

//////////////////////////////////////// COMPONENT ////////////////////////////////////////
/** The Appraisal Index tab of the Appraisals shell: the date range, the filter card, and the index. */
function AppraisalIndexTab() {
  const rooftop = useRooftop();
  const customerConfig = useCustomerConfig();

  const [disablePickers, setDisablePickers] = React.useState(getDefaultDisable());
  const [range, setRange] = React.useState(getDefaultRange());
  const [start, setStart] = React.useState(getDefaultStart());
  const [end, setEnd] = React.useState(getDefaultEnd());

  // The move types the appraisal form offers. A setting, not an entitlement:
  // this tab only renders inside the Appraisals shell, which the product
  // envelope has already gated (card #2346).
  const allowedMoveTypes = React.useMemo<string[]>(() => {
    const section = customerConfig?.appraisals as { allowed_move_types?: string[] } | undefined;
    return section?.allowed_move_types ?? [];
  }, [customerConfig]);

  const [apiLoading, setApiLoading] = React.useState(false);

  // One Clear Filters on the date row resets the range to this quarter and asks the table to drop
  // its tag and search filters.
  const [clearRequest, setClearRequest] = React.useState(0);

  const { data, loading, error, refetch } = useGetAppraisalRowsQuery({
    variables: {
      customerId: rooftop?.id || 0,
      start: start,
      end: end,
    },
  });

  // Control range picker
  const handleRangeChange = (value: string) => {
    log && console.log(`handleRangeChange() Value:`, value);
    localStorage.setItem(`appraisal-index-range`, value);
    if (value === `this`) {
      setDisablePickers(true);
      setRange(value);
      setStart(dayjs.utc(dayjs().startOf('day').subtract(3, 'months')).format());
      setEnd(dayjs.utc(dayjs().endOf('day')).format());
    } else if (value === `last`) {
      setDisablePickers(true);
      setRange(value);
      setStart(dayjs.utc(dayjs().startOf('day').subtract(6, 'months')).format());
      setEnd(dayjs.utc(dayjs().endOf('day').subtract(3, 'months')).format());
    } else {
      setDisablePickers(false);
      setRange(value);
      setStart(getDefaultStart());
      setEnd(getDefaultEnd());
    }
  };

  // Control date pickers
  const handleDateChange = (value: string, name: string) => {
    log && console.log(`Date Change:`, { value, name });

    // Set start date
    // If the start date is more than 3 months before the end date, move the end date
    // If the start date is after the end date, move the end date
    if (name === `start`) {
      const newStart = dayjs.utc(dayjs(value).startOf('day')).format();
      if (dayjs(newStart).isBefore(dayjs(end).subtract(3, 'months'))) {
        const newEnd = dayjs.utc(dayjs(newStart).endOf('day').add(3, 'months')).format();
        localStorage.setItem(`appraisal-index-end`, newEnd);
        setEnd(newEnd);
      }
      if (dayjs(newStart).isAfter(dayjs(end))) {
        const newEnd = dayjs.utc(dayjs(newStart).endOf('day')).format();
        localStorage.setItem(`appraisal-index-end`, newEnd);
        setEnd(newEnd);
      }
      localStorage.setItem(`appraisal-index-start`, newStart);
      setStart(newStart);
    }

    // Set end date
    // If the end date is before the start date, move the start date
    // If the end date is more than 3 months after the start date, move the start date
    if (name === `end`) {
      const newEnd = dayjs.utc(dayjs(value).startOf('day')).format();
      if (dayjs(newEnd).isBefore(dayjs(start))) {
        const newStart = dayjs.utc(dayjs(newEnd).startOf('day')).format();
        localStorage.setItem(`appraisal-index-start`, newStart);
        setStart(newStart);
      }
      if (dayjs(newEnd).isAfter(dayjs(start).add(3, 'months'))) {
        const newStart = dayjs.utc(dayjs(newEnd).startOf('day').subtract(3, 'months')).format();
        localStorage.setItem(`appraisal-index-start`, newStart);
        setStart(newStart);
      }
      localStorage.setItem(`appraisal-index-end`, newEnd);
      setEnd(newEnd);
    }
  };

  //TODO: These are all loading states for the table, they should be moved there
  if (apiLoading || loading) return <LinearProgress />;

  const handleClearAll = () => {
    handleRangeChange(`this`);
    setClearRequest(n => n + 1);
  };

  const content = (
    <>
      <AppraisalDateRange
        range={range}
        start={start}
        end={end}
        onRangeChange={handleRangeChange}
        onDateChange={handleDateChange}
        disablePickers={disablePickers}
        trailing={<ClearFiltersButton onClear={handleClearAll} />}
      />

      <AppraisalIndexTable
        clearRequest={clearRequest}
        appraisalVehicles={data?.appraisal_vehicles as Appraisal_Vehicles[]}
        // loading={loading}
        // error={error}
        refetch={refetch}
        allowedMoveTypes={allowedMoveTypes}
        setApiLoading={setApiLoading}
        customerId={rooftop?.id || 0}
      />
    </>
  );

  return (
    <Container maxWidth='lg' sx={{ py: 3 }}>
      {content}
    </Container>
  );
}

//////////////////////////////////////// EXPORT ////////////////////////////////////////
export default AppraisalIndexTab;
